import { Application, Container, Graphics } from 'pixi.js';

// Movement speed in pixels per millisecond.
const REPLAY_CURSOR_SPEED = 5;
// Lifetime of trail particles in milliseconds.
const TRAIL_LIFETIME = 200;

interface Trail {
  gfx: Graphics;
  age: number;
}

/**
 * Simple cursor used for replay playback. Moves toward a target point
 * at a fixed velocity and leaves a shrinking/fading trail behind.
 */
export default class ReplayCursor extends Container {
  private targets: { x: number; y: number }[] = [];
  private trails: Trail[] = [];

  constructor(private app: Application) {
    super();

    const dot = new Graphics();
    dot.circle(0, 0, 4).fill({ color: 0xff0000 });
    this.addChild(dot);

    this.app.ticker.add(this.update, this);
  }

  setTarget(x: number, y: number): void {
    this.targets.push({ x, y });
  }

  private leftover = 0;

  private update(): void {
    const dt = this.app.ticker.deltaMS;
    let step = REPLAY_CURSOR_SPEED * dt + this.leftover;
    const prevX = this.x;
    const prevY = this.y;

    if (this.targets.length > 0) {
      const { x: tx, y: ty } = this.targets[0];
      const dx = tx - this.x;
      const dy = ty - this.y;
      const dist = Math.hypot(dx, dy);
      if (dist <= step) {
        this.x = tx;
        this.y = ty;
        this.targets.shift();
        this.leftover = step - dist;
      } else {
        this.x += (dx / dist) * step;
        this.y += (dy / dist) * step;
        this.leftover = 0;
      }
    } else {
      this.leftover = 0;
    }

    this.spawnTrail(prevX, prevY);
    this.updateTrails(dt);
  }

  private spawnTrail(x: number, y: number): void {
    if (!this.parent) return;
    const trail = new Graphics();
    trail.circle(0, 0, 3).fill({ color: 0xff0000 });
    trail.position.set(x, y);
    const parent = this.parent;
    parent.addChildAt(trail, parent.getChildIndex(this));
    this.trails.push({ gfx: trail, age: 0 });
  }

  private updateTrails(dt: number): void {
    for (const t of [...this.trails]) {
      t.age += dt;
      const progress = t.age / TRAIL_LIFETIME;
      t.gfx.alpha = Math.max(1 - progress, 0);
      t.gfx.scale.set(Math.max(1 - progress, 0));
      if (t.age >= TRAIL_LIFETIME) {
        t.gfx.destroy();
        this.trails.splice(this.trails.indexOf(t), 1);
      }
    }
  }

  override destroy(options?: boolean | { children?: boolean; texture?: boolean; baseTexture?: boolean }): void {
    this.app.ticker.remove(this.update, this);
    for (const t of this.trails) t.gfx.destroy();
    this.trails = [];
    this.targets = [];
    super.destroy(options);
  }
}

